/**
 * Three sparks twinkle in turn while they are on screen: each spins in from nothing over a half
 * turn, holds, and shrinks away, on the composition's cycle of 60 frames at 25 fps. A star's cues
 * are milliseconds of that cycle on its data attributes; a cue past the cycle's end wraps, so a
 * star's animation starts its cycle at its own `grow` cue, a cycle's worth in the past.
 *
 * Both moves are a scale and a rotate on the compositor, and they loop with their holds inside:
 * nothing runs on the main thread per frame. At rest every star shows (modules/motion/onScreen.js).
 */
import {EASING_CURVES} from '../../modules/motion/easing.js';
import {playOnScreen} from '../../modules/motion/onScreen.js';

const SELECTOR = Object.freeze({
    root: '.js-motionSparks',
    star: '.js-motionSparksStar',
});

/** Milliseconds of one cycle. */
const CYCLE = 2400;

const EASE = Object.freeze({
    grow: EASING_CURVES['ease-step'],
    /* The composition's shrink curve, to the nearest named one. */
    shrink: EASING_CURVES['ease-in-out-quad'],
});

/**
 * @param {HTMLElement} root
 * @param {import('../../modules/motion/run.js').Run} run
 */
function start(root, run) {
    for (const star of root.querySelectorAll(SELECTOR.star)) {
        if (!(star instanceof SVGElement)) {
            continue;
        }
        const grow = Number(star.dataset.grow);
        /**
         * @param {string} cue
         * @returns {number} The cue's offset in the star's own cycle, which starts at its `grow`.
         */
        const at = (cue) => (Number(star.dataset[cue]) - grow) / CYCLE;
        // At the piece's start the star is a cycle less its `grow` into its own cycle.
        const timing = {duration: CYCLE, delay: grow - CYCLE, iterations: Infinity};
        run.add(
            star.animate(
                [{scale: 0, easing: EASE.grow}, {scale: 1, offset: at('full')}, {scale: 1, offset: at('shrink'), easing: EASE.shrink}, {scale: 0, offset: at('gone')}, {scale: 0}],
                timing
            ),
            star.animate([{rotate: '-180deg', easing: EASE.grow}, {rotate: '0deg', offset: at('turned')}, {rotate: '0deg'}], timing)
        );
    }
}

/**
 * Initialize every piece that is not initialized yet. Safe to call after content swaps.
 */
export function initializeComponent() {
    for (const root of document.querySelectorAll(SELECTOR.root)) {
        if (root instanceof HTMLElement && root.dataset.motionState === undefined) {
            playOnScreen(root, (run) => start(root, run));
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeComponent);
} else {
    initializeComponent();
}
document.addEventListener('htmx:load', initializeComponent);
